import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { IdentificationType, IdentificationTypeQualifier } from '../../partner/models';
import { PartnerIdentificationTypeService } from '../../partner/services/partner-identification-type.service';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoPopupModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'ep-developer-partner-identification-types',
    templateUrl: './developer-partner-identification-types.component.html',
    styleUrls: ['./developer-partner-identification-types.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoPopupModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule]
})
export class DeveloperPartnerIdentificationTypesComponent implements OnInit, AfterViewInit {

    idTypes: IdentificationType[] = [];

    allowAdding = false;
    allowUpdating = false;
    allowDeleting = false;
    allowShowLog = false;

    allowUpdatingQualifier = false;
    allowAddingQualifier = false;
    allowDeletingQualifier = false;

    constructor(
        private identificationTypeService: PartnerIdentificationTypeService,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private currentUserService: CurrentUserService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) {
    }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Developer_Action_IdentificationTypesCreate');
        this.allowUpdating = this.currentUserService.hasPermission('Developer_Action_IdentificationTypesEdit');
        this.allowDeleting = this.currentUserService.hasPermission('Developer_Action_IdentificationTypesDelete');
        this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_IdentificationTypesShowLog');

        this.allowAddingQualifier = this.allowUpdating;
        this.allowUpdatingQualifier = this.allowUpdating;
        this.allowDeletingQualifier = this.allowUpdating;
    }

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');

            this.idTypes = [];
            this.idTypes = await this.identificationTypeService.getIdentificationTypes(true);
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onInitNewRow(e: any): void {
        e.data = new IdentificationType();
    }

    onRowInserting = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as IdentificationType, item => this.saveIdentificationType(item));
    };

    onRowUpdating = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.key, ...e.newData } as IdentificationType, item => this.saveIdentificationType(item), e.newData);
    };

    onRowRemoving = (e: any): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data as IdentificationType, item => this.removeIdentificationType(item));
    };

    async saveIdentificationType(identificationType: IdentificationType): Promise<IdentificationType> {
        const newIdentificationType = await this.identificationTypeService.saveIdentificationType(identificationType);
        newIdentificationType.Qualifiers = identificationType.Qualifiers; 
        this.toastService.showToast('Identification type saved...', ToastType.success);
        return newIdentificationType;
    }

    async removeIdentificationType(identificationType: IdentificationType): Promise<IdentificationType> {
        await this.identificationTypeService.deleteIdentificationType(identificationType.IdentificationTypeId);
        this.toastService.showToast('Identification type deleted...', ToastType.success);
        return identificationType;
    }

    async onQualifierInitNewRow(e: any, parent: IdentificationType): Promise<void> {
        e.data = new IdentificationTypeQualifier(parent.IdentificationTypeId);
    }

    async onQualifierRowInsertedOrUpdated(e: any): Promise<void> {
        const item = e.data as IdentificationTypeQualifier;

        try {
            item.IdentificationTypeQualifiersId = (await this.identificationTypeService.saveIdentificationTypeQualifier(item))?.IdentificationTypeQualifiersId ?? item.IdentificationTypeQualifiersId;
            this.toastService.showToast('Qualifier saved...', ToastType.success);
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to save qualifier ...', ToastType.error);
        }
    }

    async onQualifierRowRemoved(e: any): Promise<void> {
        const item = e.data as IdentificationTypeQualifier;

        try {
            await this.identificationTypeService.deleteIdentificationTypeQualifier(item.IdentificationTypeQualifiersId);
            this.toastService.showToast('Qualifier deleted...', ToastType.success);
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to delete qualifier ...', ToastType.error);
        }
    }
}
